import { Button, PaginationType, addToast } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card } from 'reactstrap';
import { PathsLayouts } from '../../../../../config/routes';
import { downloadFromBlob } from '../../../../../utils/blob';
import { useAdminAccountLoader } from './parts/AdminAccountLoader';
import { SearchControls } from './parts/SearchControls';
import UserTable from './parts/UserTable';
import { downloadAdminAccountList } from './services';
import { ITEMS_PER_PAGE_DEFAULT } from './types/Users';

export default function UserList({ dictPrefix }: { dictPrefix: string }) {
  const { error, loading, data, query, updateQuery } = useAdminAccountLoader();
  const history = useHistory();
  const { t } = useTranslation();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const paginationTable = useMemo<PaginationType | undefined>(() => {
    if (data) {
      return {
        totalItems: data.total,
        onChangePage: (page) => updateQuery({ ...query, page }),
        itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
        totalPages: data.total_pages,
        currentPage: query.page,
      };
    } else {
      return undefined;
    }
  }, [data, query, updateQuery]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    const result = await downloadAdminAccountList(query?.searchParams ?? {});
    if (result.status === 204) {
      addToast({
        color: 'warning',
        icon: 'warning',
        text: t(`${dictPrefix}.generatingFile.empty`),
      });
    } else if (result.error || !result.data) {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t(`${dictPrefix}.generatingFile.errorOnDownload`),
      });
    } else {
      downloadFromBlob(
        result.data,
        `${t(`${dictPrefix}.generatingFile.fileName`)}.xlsx`,
      );
      addToast({
        color: 'success',
        icon: 'success',
        text: t(`${dictPrefix}.generatingFile.download`),
      });
    }
    setIsDownloading(false);
  }, [dictPrefix, query, t]);

  return (
    <Card className="mx-3 p-4">
      <div className="mb-5">
        <SearchControls dictPrefix={`${dictPrefix}.searchControls`} />

        <div className="d-flex flex-justify-content">
          <Button
            className="ml-auto"
            type="button"
            size="md"
            icon="download"
            outlined
            loading={isDownloading}
            text={t(`${dictPrefix}.generatingFile.button`)}
            onClick={handleDownload}
          />
          <Button
            className="ml-3"
            size="md"
            text={'crear nuevo usuario'}
            icon="plus"
            onClick={() => {
              history.push(`${PathsLayouts.usersAndRoles}/users/create/`);
            }}
          />
        </div>
      </div>

      <UserTable
        dictPrefix={`${dictPrefix}.table`}
        isLoadingResults={loading}
        data={data?.data ?? []}
        pagination={paginationTable}
        hasError={Boolean(error)}
        hasFilters={Object.values(query).some((value) => value !== null)}
      />
    </Card>
  );
}
